import api from './api';
import { CustomerWithStats, CustomerStats, CustomerBooking, CustomerNote, CustomerFilters } from '../types/customer';

export interface CustomersResponse {
  customers: CustomerWithStats[];
  total: number;
  page: number;
  limit: number;
}

export const customerService = {
  // Get customers with stats (paginated)
  async getCustomersWithStats(filters: CustomerFilters): Promise<CustomersResponse> {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.tag && filters.tag !== 'all') params.append('tag', filters.tag);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.status === 'blacklisted') params.append('isBlacklisted', 'true');
    if (filters.minBookings !== undefined) params.append('minBookings', String(filters.minBookings));
    if (filters.maxBookings !== undefined) params.append('maxBookings', String(filters.maxBookings));
    if (filters.minSpent !== undefined) params.append('minSpent', String(filters.minSpent));
    if (filters.maxSpent !== undefined) params.append('maxSpent', String(filters.maxSpent));
    if (filters.lastBookingFrom) params.append('lastBookingFrom', filters.lastBookingFrom);
    if (filters.lastBookingTo) params.append('lastBookingTo', filters.lastBookingTo);

    const response = await api.get(`/api/customers/with-stats?${params.toString()}`);
    return response.data as CustomersResponse;
  },

  // Get customer statistics for dashboard cards
  async getCustomerStats(): Promise<CustomerStats> {
    const response = await api.get('/api/customers/stats');
    return response.data as CustomerStats;
  },

  // Get single customer
  async getCustomer(id: string): Promise<CustomerWithStats> {
    const response = await api.get(`/api/customers/${id}`);
    return response.data as CustomerWithStats;
  },

  // Get customer booking history
  async getCustomerBookingHistory(customerId: string): Promise<CustomerBooking[]> {
    const response = await api.get(`/api/customers/${customerId}/bookings`);
    return response.data as CustomerBooking[];
  },

  // Update customer
  async updateCustomer(id: string, data: Partial<CustomerWithStats>): Promise<CustomerWithStats> {
    const response = await api.patch(`/api/customers/${id}`, data);
    return response.data as CustomerWithStats;
  },

  // Update customer tags
  async updateTags(id: string, tags: string[]): Promise<CustomerWithStats> {
    const response = await api.patch(`/api/customers/${id}/tags`, { tags });
    return response.data as CustomerWithStats;
  },

  // Blacklist/unblacklist customer
  async setBlacklist(id: string, isBlacklisted: boolean, reason?: string): Promise<CustomerWithStats> {
    const response = await api.patch(`/api/customers/${id}/blacklist`, { isBlacklisted, reason });
    return response.data as CustomerWithStats;
  },

  // Update internal notes
  async updateInternalNotes(id: string, internalNotes: string): Promise<CustomerWithStats> {
    const response = await api.patch(`/api/customers/${id}/notes`, { internalNotes });
    return response.data as CustomerWithStats;
  },

  // Customer Notes (Communication Log)
  async getCustomerNotes(customerId: string): Promise<CustomerNote[]> {
    const response = await api.get(`/api/customers/${customerId}/notes`);
    return response.data as CustomerNote[];
  },

  async addCustomerNote(customerId: string, note: string, createdBy: string, type: string = 'general'): Promise<CustomerNote> {
    const response = await api.post(`/api/customers/${customerId}/notes`, { note, createdBy, type });
    return response.data as CustomerNote;
  },

  async deleteCustomerNote(noteId: string): Promise<void> {
    await api.delete(`/api/customers/notes/${noteId}`);
  },

  // Delete customer permanently from database
  async deleteCustomer(id: string): Promise<void> {
    await api.delete(`/api/customers/${id}`);
  },

  // Export customers to CSV
  exportToCSV(customers: CustomerWithStats[]): void {
    const headers = ['Name', 'Email', 'Phone', 'License', 'Bookings', 'Total Spent', 'Status', 'Tags', 'Last Booking'];
    const rows = customers.map(c => [
      `${c.firstName} ${c.lastName}`,
      c.email,
      c.contact,
      c.licenseNumber,
      c.bookingCount?.toString() || '0',
      `$${c.totalSpent?.toFixed(2) || '0.00'}`,
      c.customerStatus || 'new',
      c.tags?.join(', ') || '',
      c.lastBookingDate ? new Date(c.lastBookingDate).toLocaleDateString() : 'Never',
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `customers_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  },
};
